import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

export interface AnalyticsMetrics {
  // Métricas globais (acumuladas)
  totalUsers: number;
  totalCompanies: number;
  totalActiveJobs: number;
  totalJobViews: number;
  
  // Métricas do período selecionado
  newUsersInPeriod: number;
  newCompaniesInPeriod: number;
  postsInPeriod: number;
  jobViewsInPeriod: number;
  likesInPeriod: number;
  commentsInPeriod: number;
  commentLikesInPeriod: number;
  datasetClicksInPeriod: number;
  challengeAttemptsInPeriod: number;
  correctChallengesInPeriod: number;
  certificatesIssuedInPeriod: number;
  pwaInstallsTotal: number;
  pwaInstallsInPeriod: number;
  
  // Dados de crescimento
  usersGrowth: { date: string; count: number }[];
  companiesGrowth: { date: string; count: number }[];
  topJobs: { id: string; title: string; views: number; company: string }[];
  engagement: { date: string; posts: number; likes: number; comments: number }[];
  userDistribution: { name: string; value: number }[];
  externalLinks: { title: string; url: string; clicks: number }[];
  
  // Lista de empresas cadastradas
  companiesList: { name: string; createdAt: string; activeJobs: number }[];

  // Receita
  mrr: number;
  activeSubscriptionsCount: number;
  subscriptionsByPlan: { plan: string; count: number; revenue: number }[];
  revenueInPeriod: number;
  paymentsInPeriod: number;
  subscriptionsList: { companyName: string; planType: string; status: string; currentPeriodEnd: string | null }[];

  // Assinaturas de candidatos
  candidateSubsActive: number;
  candidateTrialsActive: number;
  candidatePremiumMonthly: number;
  candidatePremiumYearly: number;
  candidateMrr: number;
  candidateNewPremiumInPeriod: number;
  candidateNewTrialsInPeriod: number;
  candidateConversionsInPeriod: number;
  candidateSubscriptionsByPlan: { plan: string; count: number; revenue: number }[];
}

export function useAnalyticsData(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['analytics-data', startDate, endDate],
    queryFn: async (): Promise<AnalyticsMetrics> => {
      // Usar formato ISO com timezone UTC para consistência
      const startDateTime = `${startDate}T00:00:00.000Z`;
      const endDateTime = `${endDate}T23:59:59.999Z`;

      // ===== MÉTRICAS GLOBAIS (não afetadas pelo filtro) =====
      
      // Total de usuários (candidatos) - acumulado
      const { count: totalUsers } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('user_type', 'candidate');

      // Total de empresas - acumulado
      const { count: totalCompanies } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('user_type', 'company');

      // Total de visualizações de vagas (acumulado global)
      const { data: jobsData } = await supabase
        .from('jobs')
        .select('views_count')
        .eq('status', 'active')
        .is('deleted_at', null);

      const normalJobViews = jobsData?.reduce((sum, job) => sum + (job.views_count || 0), 0) || 0;

      const { data: curatedJobsData } = await supabase
        .from('curated_jobs')
        .select('views_count')
        .eq('status', 'active')
        .is('deleted_at', null);

      const curatedJobViews = curatedJobsData?.reduce((sum, job) => sum + (job.views_count || 0), 0) || 0;
      const totalJobViews = normalJobViews + curatedJobViews;

      // Total de vagas ativas (snapshot atual)
      const { count: normalActiveJobs } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active')
        .is('deleted_at', null);

      const { count: curatedActiveJobs } = await supabase
        .from('curated_jobs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active')
        .is('deleted_at', null);

      const totalActiveJobs = (normalActiveJobs || 0) + (curatedActiveJobs || 0);

      // ===== MÉTRICAS DO PERÍODO =====

      // Novos candidatos no período
      const { count: newUsersInPeriod } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('user_type', 'candidate')
        .gte('created_at', startDateTime)
        .lte('created_at', endDateTime);

      // Novas empresas no período
      const { count: newCompaniesInPeriod } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('user_type', 'company')
        .gte('created_at', startDateTime)
        .lte('created_at', endDateTime);

      // Posts criados no período
      const { count: postsInPeriod } = await supabase
        .from('community_posts')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startDateTime)
        .lte('created_at', endDateTime);

      // Visualizações de vagas no período (usando job_views)
      const { count: jobViewsNormal } = await supabase
        .from('job_views')
        .select('*', { count: 'exact', head: true })
        .gte('viewed_at', startDateTime)
        .lte('viewed_at', endDateTime);

      const { count: jobViewsCurated } = await supabase
        .from('curated_job_views')
        .select('*', { count: 'exact', head: true })
        .gte('viewed_at', startDateTime)
        .lte('viewed_at', endDateTime);

      const jobViewsInPeriod = (jobViewsNormal || 0) + (jobViewsCurated || 0);

      // Curtidas em posts no período
      const { count: likesInPeriod } = await supabase
        .from('community_post_likes')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startDateTime)
        .lte('created_at', endDateTime);

      // Comentários no período
      const { count: commentsInPeriod } = await supabase
        .from('community_post_comments')
        .select('*', { count: 'exact', head: true })
        .is('deleted_at', null)
        .gte('created_at', startDateTime)
        .lte('created_at', endDateTime);

      // Curtidas em comentários no período
      const { count: commentLikesInPeriod } = await supabase
        .from('community_comment_likes')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startDateTime)
        .lte('created_at', endDateTime);

      // Cliques em datasets no período
      const { count: datasetClicksInPeriod } = await supabase
        .from('dataset_clicks' as any)
        .select('*', { count: 'exact', head: true })
        .gte('clicked_at', startDateTime)
        .lte('clicked_at', endDateTime);

      // Tentativas de desafios no período
      const { count: challengeAttemptsInPeriod } = await supabase
        .from('user_challenge_attempts')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startDateTime)
        .lte('created_at', endDateTime);

      // Desafios corretos no período
      const { count: correctChallengesInPeriod } = await supabase
        .from('user_challenge_attempts')
        .select('*', { count: 'exact', head: true })
        .eq('is_correct', true)
        .gte('created_at', startDateTime)
        .lte('created_at', endDateTime);

      // Certificados emitidos no período
      const { count: certificatesIssuedInPeriod } = await supabase
        .from('challenge_certificates')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startDateTime)
        .lte('created_at', endDateTime);

      // Instalações do PWA
      const { count: pwaInstallsTotal } = await supabase
        .from('pwa_installs' as any)
        .select('*', { count: 'exact', head: true });

      const { count: pwaInstallsInPeriod } = await supabase
        .from('pwa_installs' as any)
        .select('*', { count: 'exact', head: true })
        .gte('installed_at', startDateTime)
        .lte('installed_at', endDateTime);

      // ===== DADOS DE CRESCIMENTO =====

      // Crescimento de usuários por dia
      const { data: usersGrowthData } = await supabase
        .from('profiles')
        .select('created_at')
        .eq('user_type', 'candidate')
        .gte('created_at', startDateTime)
        .lte('created_at', endDateTime)
        .order('created_at', { ascending: true });

      const usersGrowthMap = new Map<string, number>();
      usersGrowthData?.forEach(user => {
        const date = format(new Date(user.created_at), 'dd/MM');
        usersGrowthMap.set(date, (usersGrowthMap.get(date) || 0) + 1);
      });
      const usersGrowth = Array.from(usersGrowthMap.entries()).map(([date, count]) => ({ date, count }));

      // Crescimento de empresas por dia
      const { data: companiesGrowthData } = await supabase
        .from('profiles')
        .select('created_at')
        .eq('user_type', 'company')
        .gte('created_at', startDateTime)
        .lte('created_at', endDateTime)
        .order('created_at', { ascending: true });

      const companiesGrowthMap = new Map<string, number>();
      companiesGrowthData?.forEach(company => {
        const date = format(new Date(company.created_at), 'dd/MM');
        companiesGrowthMap.set(date, (companiesGrowthMap.get(date) || 0) + 1);
      });
      const companiesGrowth = Array.from(companiesGrowthMap.entries()).map(([date, count]) => ({ date, count }));

      // Top 10 vagas por views (acumulado)
      const { data: topNormalJobsData } = await supabase
        .from('jobs')
        .select(`
          id,
          title,
          views_count,
          companies (name)
        `)
        .eq('status', 'active')
        .is('deleted_at', null)
        .order('views_count', { ascending: false })
        .limit(10);

      const { data: topCuratedJobsData } = await supabase
        .from('curated_jobs')
        .select('id, title, views_count, company_name')
        .eq('status', 'active')
        .is('deleted_at', null)
        .order('views_count', { ascending: false })
        .limit(10);

      const normalJobs = topNormalJobsData?.map(job => ({
        id: job.id,
        title: job.title,
        views: job.views_count || 0,
        company: (job.companies as any)?.name || 'N/A'
      })) || [];

      const curatedJobs = topCuratedJobsData?.map(job => ({
        id: job.id,
        title: job.title,
        views: job.views_count || 0,
        company: job.company_name || 'N/A'
      })) || [];

      const topJobs = [...normalJobs, ...curatedJobs]
        .sort((a, b) => b.views - a.views)
        .slice(0, 10);

      // Engajamento da comunidade por data
      const { data: engagementData } = await supabase
        .from('community_posts')
        .select('created_at, likes_count, comments_count')
        .gte('created_at', startDateTime)
        .lte('created_at', endDateTime)
        .order('created_at', { ascending: true });

      const engagementMap = new Map<string, { posts: number; likes: number; comments: number }>();
      engagementData?.forEach(post => {
        const date = format(new Date(post.created_at), 'dd/MM');
        const current = engagementMap.get(date) || { posts: 0, likes: 0, comments: 0 };
        engagementMap.set(date, {
          posts: current.posts + 1,
          likes: current.likes + (post.likes_count || 0),
          comments: current.comments + (post.comments_count || 0)
        });
      });
      const engagement = Array.from(engagementMap.entries()).map(([date, data]) => ({ date, ...data }));

      // Distribuição de usuários (global)
      const userDistribution = [
        { name: 'Candidatos', value: totalUsers || 0 },
        { name: 'Empresas', value: totalCompanies || 0 }
      ];

      // Links externos com cliques no período
      const { data: linksData } = await supabase
        .from('community_external_links')
        .select('id, title, url, order_index')
        .eq('is_active', true)
        .order('order_index', { ascending: true });

      const externalLinks = await Promise.all(
        (linksData || []).map(async (link) => {
          const { count } = await supabase
            .from('external_link_clicks')
            .select('*', { count: 'exact', head: true })
            .eq('link_id', link.id)
            .gte('clicked_at', startDateTime)
            .lte('clicked_at', endDateTime);

          return {
            title: link.title,
            url: link.url,
            clicks: count || 0
          };
        })
      );

      // ===== LISTA DE EMPRESAS CADASTRADAS =====
      const { data: companiesListData } = await supabase
        .from('companies')
        .select('id, name, created_at')
        .order('created_at', { ascending: false });

      const companiesList = await Promise.all(
        (companiesListData || []).map(async (company) => {
          const { count } = await supabase
            .from('jobs')
            .select('*', { count: 'exact', head: true })
            .eq('company_id', company.id)
            .eq('status', 'active')
            .is('deleted_at', null);
          
          return {
            name: company.name,
            createdAt: company.created_at,
            activeJobs: count || 0
          };
        })
      );

      // ===== RECEITA =====
      const PLAN_PRICES: Record<string, number> = { core: 399, plus: 699 };

      // Assinaturas ativas
      const { data: activeSubscriptions } = await supabase
        .from('company_subscriptions')
        .select('company_id, plan_type, status, current_period_end')
        .eq('status', 'active');

      const subsData = activeSubscriptions || [];
      const mrr = subsData.reduce((sum, s) => sum + (PLAN_PRICES[s.plan_type] || 0), 0);
      const activeSubscriptionsCount = subsData.length;

      const planCounts = new Map<string, { count: number; revenue: number }>();
      subsData.forEach(s => {
        const current = planCounts.get(s.plan_type) || { count: 0, revenue: 0 };
        planCounts.set(s.plan_type, {
          count: current.count + 1,
          revenue: current.revenue + (PLAN_PRICES[s.plan_type] || 0)
        });
      });
      const subscriptionsByPlan = Array.from(planCounts.entries()).map(([plan, data]) => ({
        plan: plan.charAt(0).toUpperCase() + plan.slice(1),
        ...data
      }));

      // Buscar nomes das empresas para a lista de assinaturas
      const subscriptionsList = await Promise.all(
        subsData.map(async (sub) => {
          const { data: companyData } = await supabase
            .from('companies')
            .select('name')
            .eq('id', sub.company_id)
            .maybeSingle();

          return {
            companyName: companyData?.name || 'N/A',
            planType: sub.plan_type,
            status: sub.status,
            currentPeriodEnd: sub.current_period_end
          };
        })
      );

      // Pagamentos no período
      const { data: paymentsData } = await supabase
        .from('job_payments')
        .select('amount')
        .eq('status', 'paid')
        .gte('created_at', startDateTime)
        .lte('created_at', endDateTime);

      const revenueInPeriod = (paymentsData || []).reduce((sum, p) => sum + (p.amount / 100), 0);
      const paymentsInPeriod = (paymentsData || []).length;

      // ===== ASSINATURAS DE CANDIDATOS =====
      // Preços placeholder — ajustar conforme os planos reais
      const CANDIDATE_PLAN_PRICES: Record<string, number> = {
        premium: 29,
        premium_yearly: 290 / 12,
      };

      const nowIso = new Date().toISOString();

      const { data: candidateSubs } = await supabase
        .from('user_subscriptions')
        .select('user_id, plan_type, status, trial_started_at, trial_ends_at, premium_started_at, premium_ends_at');

      const cSubs = candidateSubs || [];
      const isActiveRow = (s: any) => {
        if (s.status !== 'active') return false;
        if (s.plan_type === 'trial') {
          return !s.trial_ends_at || s.trial_ends_at > nowIso;
        }
        return !s.premium_ends_at || s.premium_ends_at > nowIso;
      };

      const activeCandidateSubs = cSubs.filter(isActiveRow);
      const candidateTrialsActive = activeCandidateSubs.filter(s => (s.plan_type as string) === 'trial').length;
      const candidatePremiumMonthly = activeCandidateSubs.filter(s => (s.plan_type as string) === 'premium').length;
      const candidatePremiumYearly = activeCandidateSubs.filter(s => (s.plan_type as string) === 'premium_yearly').length;
      const candidateSubsActive = activeCandidateSubs.length;
      const candidateMrr =
        candidatePremiumMonthly * (CANDIDATE_PLAN_PRICES.premium || 0) +
        candidatePremiumYearly * (CANDIDATE_PLAN_PRICES.premium_yearly || 0);

      const inPeriod = (iso: string | null | undefined) =>
        !!iso && iso >= startDateTime && iso <= endDateTime;

      const candidateNewPremiumInPeriod = cSubs.filter(
        s => ((s.plan_type as string) === 'premium' || (s.plan_type as string) === 'premium_yearly') && inPeriod(s.premium_started_at)
      ).length;
      const candidateNewTrialsInPeriod = cSubs.filter(s => inPeriod(s.trial_started_at)).length;
      const candidateConversionsInPeriod = cSubs.filter(
        s =>
          ((s.plan_type as string) === 'premium' || (s.plan_type as string) === 'premium_yearly') &&
          inPeriod(s.premium_started_at) &&
          !!s.trial_started_at
      ).length;

      const candidatePlanCounts = new Map<string, { count: number; revenue: number }>();
      activeCandidateSubs.forEach(s => {
        const cur = candidatePlanCounts.get(s.plan_type) || { count: 0, revenue: 0 };
        candidatePlanCounts.set(s.plan_type, {
          count: cur.count + 1,
          revenue: cur.revenue + (CANDIDATE_PLAN_PRICES[s.plan_type] || 0),
        });
      });
      const candidateSubscriptionsByPlan = Array.from(candidatePlanCounts.entries()).map(([plan, d]) => ({
        plan: plan.charAt(0).toUpperCase() + plan.slice(1),
        ...d,
      }));

      return {
        // Globais
        totalUsers: totalUsers || 0,
        totalCompanies: totalCompanies || 0,
        totalActiveJobs,
        totalJobViews,
        
        // Do período
        newUsersInPeriod: newUsersInPeriod || 0,
        newCompaniesInPeriod: newCompaniesInPeriod || 0,
        postsInPeriod: postsInPeriod || 0,
        jobViewsInPeriod,
        likesInPeriod: likesInPeriod || 0,
        commentsInPeriod: commentsInPeriod || 0,
        commentLikesInPeriod: commentLikesInPeriod || 0,
        datasetClicksInPeriod: datasetClicksInPeriod || 0,
        challengeAttemptsInPeriod: challengeAttemptsInPeriod || 0,
        correctChallengesInPeriod: correctChallengesInPeriod || 0,
        certificatesIssuedInPeriod: certificatesIssuedInPeriod || 0,
        pwaInstallsTotal: pwaInstallsTotal || 0,
        pwaInstallsInPeriod: pwaInstallsInPeriod || 0,
        
        // Dados de crescimento
        usersGrowth,
        companiesGrowth,
        topJobs,
        engagement,
        userDistribution,
        externalLinks,
        companiesList,

        // Receita
        mrr,
        activeSubscriptionsCount,
        subscriptionsByPlan,
        revenueInPeriod,
        paymentsInPeriod,
        subscriptionsList,

        // Assinaturas de candidatos
        candidateSubsActive,
        candidateTrialsActive,
        candidatePremiumMonthly,
        candidatePremiumYearly,
        candidateMrr,
        candidateNewPremiumInPeriod,
        candidateNewTrialsInPeriod,
        candidateConversionsInPeriod,
        candidateSubscriptionsByPlan,
      };
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}
